import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database as BaseDatabase, Json } from '@auctisync/shared';
import { supabase } from './supabase';

/**
 * Module #21 Bidder CRM uses a view + table + RPC that landed in
 * 2026-05-11 migrations and aren't yet in @auctisync/shared's generated
 * Database type (`supabase gen types` regen pending).
 *
 * Rather than spray `as any` and ESLint-disables across every Module #21
 * page, this module augments the Database type with the new substrate
 * and exposes a typed wrapper around the existing supabase client.
 *
 * Remove this file (and replace `supabaseCrm` imports with `supabase`)
 * once `supabase gen types typescript --linked` lands the regen.
 */

// ─── Row / Insert / Update / Args types ─────────────────────────────────────

export interface BidderLifetimeStatsRow {
  tenant_buyer_id: string;
  tenant_id: string;
  user_id: string;
  tenant_bidder_number: number | null;
  status: string;
  tenant_tags: string[] | null;
  tenant_notes: string | null;
  approved_at: string | null;
  cc_preauth_verified_at: string | null;
  first_bid_at: string | null;
  last_bid_at: string | null;
  total_bids: number;
  total_purchases: number;
  total_spend_cents: number;
  lifetime_refund_cents: number;
  total_invoices: number;
  voided_invoice_count: number;
  avg_bid_cents: number | null;
  days_since_last_bid: number | null;
  no_show_rate_pct: string | null;
  created_at: string;
  updated_at: string;
}

export interface BidderSegmentRow {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  filter_definition: Json;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface BidderSegmentInsert {
  id?: string;
  tenant_id: string;
  name: string;
  description?: string | null;
  filter_definition?: Json;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface BidderSegmentUpdate {
  name?: string;
  description?: string | null;
  filter_definition?: Json;
  updated_at?: string;
}

// ─── Database type augmentation ─────────────────────────────────────────────

type PublicSchema = BaseDatabase['public'];

export type DatabaseWithCrm = Omit<BaseDatabase, 'public'> & {
  public: Omit<PublicSchema, 'Tables' | 'Views' | 'Functions'> & {
    Tables: PublicSchema['Tables'] & {
      bidder_segments: {
        Row: BidderSegmentRow;
        Insert: BidderSegmentInsert;
        Update: BidderSegmentUpdate;
        Relationships: [];
      };
    };
    Views: PublicSchema['Views'] & {
      bidder_lifetime_stats: {
        Row: BidderLifetimeStatsRow;
        Relationships: [];
      };
    };
    Functions: PublicSchema['Functions'] & {
      resolve_segment_member_ids: {
        Args: { p_segment_id: string };
        Returns: string[];
      };
    };
  };
};

/**
 * Typed Supabase client for Module #21 substrate. Cast through unknown is
 * safe — same underlying client, the only change is the static type
 * surface visible to TypeScript.
 */
export const supabaseCrm = supabase as unknown as SupabaseClient<DatabaseWithCrm>;

// ─── Typed RPC wrappers ─────────────────────────────────────────────────────
// Supabase-js's `.rpc<FnName>` overload resolution doesn't always pick up
// augmented Functions entries cleanly when the Database type is built via
// intersection. Wrap the RPC call here so the rest of the codebase gets
// proper types without per-call casts.

type RpcResult<T> = { data: T | null; error: { message: string } | null };

/**
 * Resolve a bidder segment to its current tenant_buyer_id members.
 * PostgREST returns SETOF uuid for this function — surfaced as `string[]`
 * after parsing the array of `{ resolve_segment_member_ids: uuid }` rows.
 */
export async function resolveSegmentMemberIds(p_segment_id: string): Promise<RpcResult<string[]>> {
  const rpc = (
    supabase.rpc as unknown as (
      fn: 'resolve_segment_member_ids',
      args: { p_segment_id: string },
    ) => Promise<RpcResult<unknown>>
  )('resolve_segment_member_ids', { p_segment_id });
  const result = await rpc;
  if (result.error) return { data: null, error: result.error };

  // PostgREST shape: array of single-column rows when the function returns
  // SETOF scalar. Each row is { resolve_segment_member_ids: uuid } OR (in
  // some supabase-js versions) just the raw string. Handle both.
  const raw = Array.isArray(result.data) ? result.data : [];
  const ids = raw
    .map((r) => {
      if (typeof r === 'string') return r;
      if (r && typeof r === 'object' && 'resolve_segment_member_ids' in r) {
        return (r as { resolve_segment_member_ids: string }).resolve_segment_member_ids;
      }
      return null;
    })
    .filter((x): x is string => typeof x === 'string');

  return { data: ids, error: null };
}

/**
 * Add a tag to N bidders atomically. Idempotent — skips bidders that
 * already have the tag. Returns count of rows affected.
 */
export async function addTagToBidders(
  tenantBuyerIds: string[],
  tag: string,
): Promise<RpcResult<number>> {
  const rpc = (
    supabase.rpc as unknown as (
      fn: 'add_tag_to_bidders',
      args: { p_tenant_buyer_ids: string[]; p_tag: string },
    ) => Promise<RpcResult<unknown>>
  )('add_tag_to_bidders', { p_tenant_buyer_ids: tenantBuyerIds, p_tag: tag });
  const result = await rpc;
  if (result.error) return { data: null, error: result.error };
  return { data: typeof result.data === 'number' ? result.data : 0, error: null };
}

/**
 * Remove a tag from N bidders atomically. Returns count of rows affected.
 */
export async function removeTagFromBidders(
  tenantBuyerIds: string[],
  tag: string,
): Promise<RpcResult<number>> {
  const rpc = (
    supabase.rpc as unknown as (
      fn: 'remove_tag_from_bidders',
      args: { p_tenant_buyer_ids: string[]; p_tag: string },
    ) => Promise<RpcResult<unknown>>
  )('remove_tag_from_bidders', { p_tenant_buyer_ids: tenantBuyerIds, p_tag: tag });
  const result = await rpc;
  if (result.error) return { data: null, error: result.error };
  return { data: typeof result.data === 'number' ? result.data : 0, error: null };
}

// ─── Typed CRUD wrappers for bidder_segments ───────────────────────────────
// supabase-js's overload resolution for the augmented Database type works
// for SELECT but breaks for INSERT/UPDATE/DELETE (the Insert type resolves
// to `never`). These wrappers isolate the cast behind typed return chains.

interface ChainResult<T> {
  data: T | null;
  error: { message: string } | null;
}

interface EqChain<T> {
  eq(col: string, val: unknown): Promise<ChainResult<T>>;
}

interface SelectChain<T> {
  select(): { single(): Promise<ChainResult<T>> };
}

interface TableChain {
  insert(input: BidderSegmentInsert): SelectChain<BidderSegmentRow>;
  update(patch: BidderSegmentUpdate): EqChain<null>;
  delete(): EqChain<null>;
}

// Call supabase.from with `this` bound correctly (avoids unbound-method
// lint), then cast the returned builder to our typed chain.
const crmTable = (table: string): TableChain =>
  supabase.from(table as never) as unknown as TableChain;

export async function createBidderSegment(
  input: BidderSegmentInsert,
): Promise<RpcResult<BidderSegmentRow>> {
  return crmTable('bidder_segments').insert(input).select().single();
}

export async function updateBidderSegment(
  id: string,
  patch: BidderSegmentUpdate,
): Promise<RpcResult<null>> {
  return crmTable('bidder_segments').update(patch).eq('id', id);
}

export async function deleteBidderSegment(id: string): Promise<RpcResult<null>> {
  return crmTable('bidder_segments').delete().eq('id', id);
}

/**
 * Fan a tenant-composed broadcast email out to all current members of a
 * bidder segment. Returns count of notification rows created (existing
 * pg_net webhook fires send-notification-email for each one).
 */
export async function broadcastToSegment(
  segmentId: string,
  subject: string,
  body: string,
): Promise<RpcResult<number>> {
  const rpc = (
    supabase.rpc as unknown as (
      fn: 'broadcast_to_segment',
      args: { p_segment_id: string; p_subject: string; p_body: string },
    ) => Promise<RpcResult<unknown>>
  )('broadcast_to_segment', { p_segment_id: segmentId, p_subject: subject, p_body: body });
  const result = await rpc;
  if (result.error) return { data: null, error: result.error };
  return { data: typeof result.data === 'number' ? result.data : 0, error: null };
}
